import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#setupKachingBundlesSync();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Syncs sticky bar price and quantity when Kaching Bundles changes selection.
   * Kaching emits `variants-changed` / `items-changed` on kaching-bundles-block;
   * it does not dispatch the theme ProductSelectEvent.
   */
  #setupKachingBundlesSync() {
    const { signal } = this.#abortController;
    const productId = this.dataset.productId;

    const syncFromEvent = (/** @type {Event} */ event) => {
      const block = this.#getKachingBlockFromEvent(event);
      if (!block || !this.#isKachingBlockForProduct(block, productId)) return;
      this.#syncStickyFromKachingBlock(block, event);
    };

    const attachToBlock = (/** @type {Element} */ block) => {
      if (!(block instanceof HTMLElement) || block.dataset.kachingStickySync === 'true') return;
      if (!this.#isKachingBlockForProduct(block, productId)) return;

      block.dataset.kachingStickySync = 'true';
      this.#syncStickyFromKachingBlock(block);

      block.addEventListener('variants-changed', syncFromEvent, { signal });
      block.addEventListener('items-changed', syncFromEvent, { signal });
      block.addEventListener('deal-bar-selected', syncFromEvent, { signal });
    };

    const scanForBlocks = () => {
      document.querySelectorAll('kaching-bundles-block').forEach((block) => attachToBlock(block));
    };

    document.addEventListener('variants-changed', syncFromEvent, { signal });
    document.addEventListener('items-changed', syncFromEvent, { signal });
    document.addEventListener('deal-bar-selected', syncFromEvent, { signal });
    document.addEventListener(
      'kaching-bundles-block-loaded',
      (event) => {
        if (event.target instanceof Element) attachToBlock(event.target);
      },
      { signal }
    );
    document.addEventListener('kaching-bundles-initialized', scanForBlocks, { signal });

    scanForBlocks();

    const waitObserver = new MutationObserver(() => scanForBlocks());
    waitObserver.observe(document.body, { childList: true, subtree: true });
    signal.addEventListener('abort', () => waitObserver.disconnect());

    customElements.whenDefined('kaching-bundles-block').then(() => {
      if (signal.aborted) return;
      scanForBlocks();
    });
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #getKachingBlockFromEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    if (target.matches('kaching-bundles-block')) {
      return /** @type {HTMLElement} */ (target);
    }

    return /** @type {HTMLElement | null} */ (target.closest('kaching-bundles-block'));
  }

  /**
   * @param {Element} block
   * @param {string | undefined} productId
   */
  #isKachingBlockForProduct(block, productId) {
    if (!productId) return true;

    const kachingProductId = this.#getKachingProductId(block);
    return !kachingProductId || kachingProductId === productId;
  }

  /**
   * @param {Element} block
   * @returns {string | null}
   */
  #getKachingProductId(block) {
    const bundle = block.closest('kaching-bundle');
    const bundleProductId = bundle?.getAttribute('product-id');
    if (bundleProductId) return bundleProductId;

    try {
      const productAttr = block.getAttribute('product');
      if (!productAttr) return null;
      const product = JSON.parse(productAttr);
      return product?.id != null ? String(product.id) : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {HTMLElement} block
   * @param {Event} [event]
   */
  #syncStickyFromKachingBlock(block, event) {
    const pricing = this.#getKachingPricing(block, event);
    if (pricing?.discountedPrice) {
      this.#updateStickyPrice(pricing.discountedPrice, pricing.fullPrice);
    } else {
      this.#syncStickyPriceFromKachingDom(block);
    }

    const title = block.querySelector(
      '.kaching-bundles__bars:not(.kaching-bundles__subscriptions) .kaching-bundles__bar--selected .kaching-bundles__bar-title'
    )?.textContent?.trim();
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    if (variantTitleElement instanceof HTMLElement && title) {
      variantTitleElement.textContent = title;
      variantTitleElement.style.removeProperty('display');
    }

    const quantity = this.#getKachingQuantity(block, event);
    if (quantity > 0 && quantity !== this.#currentQuantity) {
      this.#currentQuantity = quantity;
      this.#updateButtonText();
    }
  }

  /**
   * @param {HTMLElement} block
   * @param {Event} [event]
   * @returns {{ discountedPrice?: string, fullPrice?: string } | null}
   */
  #getKachingPricing(block, event) {
    const detail = /** @type {{ pricing?: Record<string, { formatted?: string, amount?: number }> } | undefined} */ (
      event?.detail
    );

    if (detail?.pricing) {
      const { discountedPrice, fullPrice } = detail.pricing;
      if (discountedPrice?.formatted) {
        return {
          discountedPrice: discountedPrice.formatted,
          fullPrice: fullPrice?.formatted,
        };
      }
    }

    const pricingFn = /** @type {{ pricing?: () => Record<string, number> } | undefined} */ (block).pricing;
    if (typeof pricingFn !== 'function') return null;

    const pricing = pricingFn.call(block);
    if (!pricing) return null;

    const selectedBar = block.querySelector('.kaching-bundles__bar--selected .kaching-bundles__bar-price');
    const selectedCompare = block.querySelector('.kaching-bundles__bar--selected .kaching-bundles__bar-full-price');

    return {
      discountedPrice: selectedBar?.textContent?.trim() || this.#formatKachingMoney(pricing.discountedPrice),
      fullPrice: selectedCompare?.textContent?.trim() || this.#formatKachingMoney(pricing.fullPrice),
    };
  }

  /**
   * @param {number | undefined} cents
   * @returns {string | undefined}
   */
  #formatKachingMoney(cents) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return undefined;
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * @param {HTMLElement} block
   */
  #syncStickyPriceFromKachingDom(block) {
    const selectedBar = block.querySelector(
      '.kaching-bundles__bars:not(.kaching-bundles__subscriptions) .kaching-bundles__bar--selected'
    );
    if (!(selectedBar instanceof Element)) return;

    const price = selectedBar.querySelector('.kaching-bundles__bar-price')?.textContent?.trim();
    const compareAt = selectedBar.querySelector('.kaching-bundles__bar-full-price')?.textContent?.trim();
    if (price) this.#updateStickyPrice(price, compareAt);
  }

  /**
   * @param {HTMLElement} block
   * @param {Event} [event]
   * @returns {number}
   */
  #getKachingQuantity(block, event) {
    const itemsFn = /** @type {{ items?: () => Array<{ quantity?: number }> } | undefined} */ (block).items;
    if (typeof itemsFn === 'function') {
      const items = itemsFn.call(block);
      const total = items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
      if (total > 0) return total;
    }

    const productForm = this.#getProductForm();
    const quantityInput = productForm?.querySelector('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement) {
      const qty = parseInt(quantityInput.value, 10);
      if (Number.isFinite(qty) && qty > 0) return qty;
    }

    const dealBarId =
      /** @type {{ dealBarId?: string | null } | undefined} */ (event?.detail)?.dealBarId ??
      block
        .querySelector(
          '.kaching-bundles__bars:not(.kaching-bundles__subscriptions) .kaching-bundles__bar--selected'
        )
        ?.getAttribute('data-deal-bar-id');

    return this.#getKachingDealBarQuantity(block, dealBarId) ?? 1;
  }

  /**
   * @param {Element} kachingBlock
   * @param {string | null} dealBarId
   * @returns {number | null}
   */
  #getKachingDealBarQuantity(kachingBlock, dealBarId) {
    if (!dealBarId) return null;

    try {
      const config = kachingBlock.getAttribute('deal-block');
      if (!config) return null;

      const dealBlock = JSON.parse(config);
      const bar = dealBlock.dealBars?.find((/** @type {{ id: string }} */ entry) => entry.id === dealBarId);
      return typeof bar?.quantity === 'number' ? bar.quantity : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} price
   * @param {string | undefined} compareAt
   */
  #updateStickyPrice(price, compareAt) {
    const priceContainer = this.querySelector('.sticky-add-to-cart__price');
    if (!priceContainer) return;

    const showCompare = Boolean(compareAt && compareAt !== price);
    const salePriceEl = priceContainer.querySelector('.price__sale .price-item--sale.price, .price__sale .price');
    const comparePriceEl = priceContainer.querySelector(
      '.price__sale .compare-at-price, .price__sale .price-item--regular'
    );
    const regularPriceEl = priceContainer.querySelector('.price__regular .price');
    const saleDiv = priceContainer.querySelector('.price__sale');
    const regularDiv = priceContainer.querySelector('.price__regular');

    if (salePriceEl instanceof HTMLElement && comparePriceEl instanceof HTMLElement) {
      salePriceEl.textContent = price;
      comparePriceEl.textContent = compareAt || '';

      if (saleDiv instanceof HTMLElement && regularDiv instanceof HTMLElement) {
        saleDiv.classList.toggle('price__hidden', !showCompare);
        regularDiv.classList.toggle('price__hidden', showCompare);
      }
      return;
    }

    if (regularPriceEl instanceof HTMLElement) {
      regularPriceEl.textContent = price;
      return;
    }

    const fallbackPriceEl = priceContainer.querySelector('.price');
    if (fallbackPriceEl instanceof HTMLElement) {
      fallbackPriceEl.textContent = price;
    }
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
